"use client"

import * as React from "react"
import { RefreshCw } from "lucide-react"

import { api, type Health } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import { AppShell, type Crumb } from "@/components/app-shell"
import { Button } from "@/components/ui/button"

/**
 * Every page's frame: sidebar, breadcrumb trail, and the API status pill.
 *
 * The pill lived in the old single-page component. Now that each section is a
 * real route, it belongs here — otherwise every page would re-implement it.
 */
export function PageShell({
  crumbs,
  children,
}: {
  crumbs: Crumb[]
  children: React.ReactNode
}) {
  // Firebase restores the session asynchronously. Fetching before that lands
  // sends requests with no token and they come back 401 — so wait for it.
  const { user, loading: authLoading } = useAuth()
  const [health, setHealth] = React.useState<Health | null>(null)
  const [checking, setChecking] = React.useState(true)

  const [checkKey, setCheckKey] = React.useState(0)
  React.useEffect(() => {
    if (authLoading || !user) return
    let cancelled = false
    void (async () => {
      const h = await api.health()
      if (cancelled) return
      setHealth(h.data)
      setChecking(false)
    })()
    return () => {
      cancelled = true
    }
  }, [authLoading, user, checkKey])

  const check = React.useCallback(() => {
    setChecking(true)
    setCheckKey((k) => k + 1)
  }, [])

  const online = health?.ok === true

  return (
    <AppShell
      crumbs={crumbs}
      status={
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className={
                "size-1.5 rounded-full " +
                (checking
                  ? "animate-pulse bg-muted-foreground"
                  : online
                    ? "bg-primary"
                    : "bg-destructive")
              }
            />
            <span className="hidden sm:inline">
              {checking ? "checking" : online ? "connected" : "offline"}
            </span>
          </span>
          <Button size="sm" variant="ghost" onClick={() => check()} disabled={checking}>
            <RefreshCw className={"size-3.5 " + (checking ? "animate-spin" : "")} />
          </Button>
        </div>
      }
    >
      {children}
    </AppShell>
  )
}
